import { FormViewType } from '@kinetic-ui/data-layer';
import { FieldComponentProps, FieldsContainer, FormComponentProps } from '@qfa/forms';
import ContentHeader from 'components/ContentHeader';
import { getValidItems, RouteBuilder } from 'kineticUtils';
import React, { useMemo } from 'react';
import { Route, Switch } from 'react-router-dom';

import FormSection from '../FormSection';
import { ViewOptions } from '../optionTypes';
import useFormViewStyles from './styles';
import { FormViewProps } from './types';

const FormView: React.FunctionComponent<FormViewProps> = (props) => {
  const {
    childViewsOptions,
    fieldComponents,
    routeBuilder,
    ignoreHash = false,
    sectionsOptions,
    title,
    className,
  } = props;
  const classes = useFormViewStyles(props);

  const sectionComponents: Record<string, FieldComponentProps[]> = useMemo(() => {
    // server configs may contain duplicate fieldNames in sectionOptions.fieldNames.
    // The use case for this is a field with different configuration, conditionally rendered using activeLogic.
    // this ensures we don't render the same field multiple times in the same section, even if it exists in fieldNames more than once.
    return (
      sectionsOptions?.reduce((acc, section) => {
        return {
          ...acc,
          [section.sectionId]: getValidItems(
            Array.from(new Set(section.fieldNames)),
            (name: string) => fieldComponents[name]
          ),
        };
      }, {}) ?? {}
    );
  }, [fieldComponents, sectionsOptions]);

  const formSections = sectionsOptions?.map((options) => {
    const { hashRoute, sectionId, name, children, additionalCardClasses, childrenBefore } = options;

    return (
      <FormSection
        fieldComponents={sectionComponents[sectionId]}
        hashRoute={hashRoute}
        key={`formConfigSection_${sectionId}`}
        sectionId={sectionId}
        ignoreHash={ignoreHash}
        additionalCardClasses={additionalCardClasses}
        name={name}
        childrenBefore={childrenBefore}
      >
        {children}
      </FormSection>
    );
  });

  const childViews = useMemo(() => {
    if (!childViewsOptions) {
      return null;
    }
    const childRoutes = childViewsOptions.filter((options) => options.type === FormViewType.Route);
    const childEmbedded = childViewsOptions.filter(
      (options) => options.type === FormViewType.Embedded
    );
    return (
      <>
        {childEmbedded.map((options) => (
          <FormView
            {...options}
            key={options.viewId}
            fieldComponents={fieldComponents}
            routeBuilder={routeBuilder}
          />
        ))}
        <Switch>
          {childRoutes.map((options) => (
            <FormView
              {...options}
              key={options.viewId}
              fieldComponents={fieldComponents}
              routeBuilder={routeBuilder}
            />
          ))}
        </Switch>
      </>
    );
  }, [childViewsOptions, fieldComponents, routeBuilder]);

  if (sectionsOptions?.length) {
    return (
      <>
        {title ? (
          <ContentHeader
            id={`header_${title}`}
            variant='body1'
            component='section'
            className={classes.title}
          >
            {title}
          </ContentHeader>
        ) : null}
        <div className={className}>{formSections}</div>
      </>
    );
  }

  return childViews;
};

export const renderView = (
  options: ViewOptions,
  fieldComponents: FormComponentProps,
  routeBuilder: RouteBuilder,
  ignoreHash: boolean = false,
  compactMode: boolean = false
) => {
  if (compactMode) {
    return <CompactFormView fieldComponents={fieldComponents} routeBuilder={routeBuilder} />;
  }

  const { viewId, type, paths } = options;
  const formView = (
    <FormView
      {...options}
      fieldComponents={fieldComponents}
      key={`formView-${viewId}`}
      routeBuilder={routeBuilder}
      ignoreHash={ignoreHash}
    />
  );

  if (type === FormViewType.Route) {
    return <Route path={routeBuilder(paths.join('/'))}>{formView}</Route>;
  }

  return formView;
};

const CompactFormView = ({
  fieldComponents,
  routeBuilder,
}: {
  fieldComponents: FormComponentProps;
  routeBuilder: RouteBuilder;
}) => {
  const fields = useMemo(() => {
    return Object.keys(fieldComponents)
      .filter((key) => fieldComponents[key as keyof typeof fieldComponents].props.type !== 'hidden')
      .map((key) => fieldComponents[key as keyof typeof fieldComponents].default);
  }, [fieldComponents]);

  return (
    <Route path={routeBuilder()}>
      <FieldsContainer>{fields}</FieldsContainer>
    </Route>
  );
};

export default FormView;
